/**
 * Scribe – fenêtre « Durée maximale atteinte » (décision du 04/11/2026) : une réunion enregistre DUREE_MAX_REUNION_S
 * (4 h) au plus ; à 4 h l'enregistrement s'arrête et la réunion part au compte-rendu. Fenêtre modale montée par la
 * coquille dans l'onglet qui enregistrait, panneau du dock ouvert ou non, pour que l'arrêt se voie. « Relancer un
 * enregistrement » démarre aussitôt la suite : nouvelle réunion « titre (suite) » avec la langue, le niveau et les
 * participants de la première (déjà informés) ; si la première enregistrait le son de Teams, le partage est d'abord
 * demandé de nouveau. Bouton inactif tant que les dernières parties partent (phase `envoi`). Refus (plafond de la
 * semaine, partage annulé…) : message dans la fenêtre. « Fermer » : l'avis reste dans le panneau.
 */
import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { BORNES_SCRIBE, DUREE_MAX_REUNION_S, type Reunion } from '@agora/shared';
import { Button, Dialog } from '../../design';
import { ErreurApi } from '../api/client';
import { useSocle } from '../contexte';
import { useT } from '../i18n';
import { ErreurPartage } from './prise-de-son';
import { useScribe } from './Scribe';

export function FenetreDureeMaximale() {
  const s = useScribe();
  const { t, langue } = useT();
  const { api } = useSocle();
  // Réunion dont la fenêtre a été fermée : elle ne revient pas pour elle.
  const [fermee, setFermee] = React.useState<string | null>(null);
  const [enCours, setEnCours] = React.useState(false);
  const [erreur, setErreur] = React.useState<string | null>(null);
  const reunion = s?.etat.avis === 'duree' ? s.etat.reunion : null;
  const ouverte = !!reunion && fermee !== reunion.id;
  // Langue, niveau et participants de la réunion arrêtée, lus à l'ouverture (avant le clic : rien n'attend avant le
  // partage du son de Teams, qui doit suivre le geste de l'utilisateur).
  const detail = useQuery({
    queryKey: ['scribe', 'suite', reunion?.id],
    queryFn: ({ signal }) => api.get<Reunion>(`/v1/outils/scribe/reunions/${encodeURIComponent(reunion!.id)}`, undefined, signal),
    enabled: ouverte,
    staleTime: Infinity,
    retry: false,
  });
  React.useEffect(() => { setErreur(null); }, [reunion?.id]);
  if (!s || !reunion) return null;

  const titreSuite = () => {
    const suite = (titre: string) => t('outils.scribe.dureeMax.suite', { titre });
    const surplus = suite(reunion.titre).length - BORNES_SCRIBE.titre;
    return surplus > 0 ? suite(reunion.titre.slice(0, reunion.titre.length - surplus).trim()) : suite(reunion.titre);
  };
  const relancer = async () => {
    setErreur(null);
    setEnCours(true);
    try {
      await s.relancer({
        titre: titreSuite(), langue: detail.data?.langue ?? langue, niveau: detail.data?.niveau, participantsInformes: true,
        invites: (detail.data?.invites ?? []).slice(0, BORNES_SCRIBE.invites),
      });
    } catch (e) {
      setErreur(e instanceof ErreurPartage ? t(`outils.scribe.panneau.distance.erreurs.${e.motif}`)
        : e instanceof ErreurApi && e.code === 'quota_scribe_atteint' ? t('outils.scribe.quotaAtteint')
          : t('outils.scribe.dureeMax.echec'));
    } finally {
      setEnCours(false);
    }
  };
  const envoi = s.etat.phase === 'envoi';

  return (
    <Dialog
      open={ouverte} onOpenChange={(o) => { if (!o) setFermee(reunion.id); }} size="sm"
      title={t('outils.scribe.dureeMax.titre')} closeLabel={t('commun.fermer')}
      description={t('outils.scribe.dureeMax.texte', { titre: reunion.titre, h: DUREE_MAX_REUNION_S / 3600 })}
      footer={(
        <>
          <Button variant="quiet" onClick={() => setFermee(reunion.id)}>{t('commun.fermer')}</Button>
          <Button variant="primary" icon="micro" disabled={envoi || enCours || detail.isLoading} onClick={() => void relancer()}>
            {t('outils.scribe.dureeMax.relancer')}
          </Button>
        </>
      )}
    >
      {envoi ? <p className="agx-vide" role="status">{t('outils.scribe.dureeMax.envoi')}</p> : null}
      {reunion.distance && s.distance ? <p className="agx-vide">{t('outils.scribe.dureeMax.teams')}</p> : null}
      {erreur ? <p className="agx-scribe-erreur" role="alert">{erreur}</p> : null}
    </Dialog>
  );
}
